<script lang="ts" setup>
import locales from '~/assets/data/locales.json'

interface Props {
  routeKey: string
}

const props = defineProps<Props>()
const { $config } = useNuxtApp()
const route = useRoute()
const isLoading = ref(true)
const companyList = ref()
const selection = '0abcdefghijklmnopqrstuvwxyz'
const selectedLetter = computed(() => props.routeKey)

if (isEmpty(useGetSubdomain()))
  navigateTo('', { redirectCode: 301 })

setMeta(useGetSubdomain())
getCompanyList(selectedLetter.value === '0' ? '#' : selectedLetter.value)

async function getCompanyList(letterVal: string | string[]) {
  isLoading.value = true
  const { data, error } = await useSearchApi('/companies/directory', {
    initialCache: false,
    params: {
      country: useGetSubdomain(),
      letter: letterVal,
    },
    key: 'get_company_list',
  })

  if (data.value)
    companyList.value = data.value
  isLoading.value = false
}

function setMeta(localeVal: string) {
  const countryName = locales.find(obj => obj.region === localeVal)?.label

  const seoTitle = `${countryName} Companies Starting With ${useTextCapitalize(selectedLetter.value)} - Epicareer ${countryName}`
  const seoDescription = `Find ${countryName} companies starting with ${useTextCapitalize(selectedLetter.value)}. Check their company reviews, job vacancies, salary to help you find the right job for you.`
  const robotsDefault = $config.public.ENV === 'development' || $config.public.ENV === 'staging' ? { name: 'robots', content: 'nofollow, noindex' } : { name: 'robots', content: 'index, follow' }
  useHead({
    title: seoTitle,
    meta: [
      { property: 'og:title', content: seoTitle },
      { name: 'twitter:title', content: seoTitle },
      { name: 'description', content: seoDescription },
      { property: 'og:description', content: seoDescription },
      { name: 'twitter:description', content: seoDescription },
      robotsDefault,
    ],
    link: [{ rel: 'canonical', href: useSetSubdomain(useGetSubdomain()) + route.path }],
  })
}
</script>

<template>
  <BaseAtomsSkeletonBounded class="!py-0 !xl:px-0 text-gray-900">
    <p class="text-4xl font-bold font-serif pt-6 md:pt-10">
      Company Directory
    </p>
    <div class="py-6 text-xs grid grid-cols-8 sm:grid-cols-12 gap-x-3 gap-y-6 lg:space-x-4 lg:flex items-center break-all">
      <p class="col-span-2 lg:min-w-13">
        By name
      </p>
      <NuxtLink
        v-for="(item, idx) in selection.split('')"
        :key="idx"
        :to="`/companies/all/starting-with-${useTextCapitalize(item)}`"
        :class="`uppercase text-[#5843E5] text-sm text-center ${selectedLetter === item ? 'font-bold' : ''}`"
        @click="getCompanyList(item === '0' ? '#' : item)"
      >
        {{ item === '0' ? '#' : item }}
      </NuxtLink>
    </div>
    <div class="px-4 py-6 md:py-10 bg-[#F9FAFB] text-sm">
      <div class="font-semibold mb-8 capitalize">
        {{ selectedLetter === '0' ? '#' : selectedLetter }}
      </div>
      <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-6 text-[#5843E5] font-semibold">
        <div v-for="i in 21" :key="i" :class="`animate-pulse w-[${Math.floor(Math.random() * (85 - 60 + 1)) + 60}%] bg-gray-200 h-5 rounded-sm`" />
      </div>
      <div v-else-if="!isLoading && !isEmpty(companyList)" class="grid grid-cols-1 md:grid-cols-3 gap-y-4 md:gap-y-8 gap-x-6 text-[#5843E5]">
        <div v-for="(item, idx) in companyList" :key="idx">
          <div v-if="!isEmpty(item.name)" class="grid gap-y-1">
            <NuxtLink :to="`/companies/${item.slug}`" class="font-semibold text-base">
              {{ item.name }}
            </NuxtLink>
            <NuxtLink :to="`/companies/${item.slug}/jobs-vacancy`" class="text-xs">
              {{ item.name }} Jobs
            </NuxtLink>
            <NuxtLink :to="`/companies/${item.slug}/salaries-benefits`" class="text-xs">
              {{ item.name }} Salaries
            </NuxtLink>
            <NuxtLink :to="`/companies/${item.slug}/videos-photos`" class="text-xs">
              {{ item.name }} Photos
            </NuxtLink>
          </div>
        </div>
      </div>
      <div v-else class="py-10 text-center w-76 mx-auto">
        <Icon name="ic:baseline-business" class="w-18 h-18 mb-4 text-[#5843E5]" />
        <p class="text-lg font-bold mb-2">
          No Companies Found
        </p>
        <p class="text-sm text-gray-600">
          Sorry, currently our directory does not contain companies starting with that letter
        </p>
      </div>
    </div>
  </BaseAtomsSkeletonBounded>
</template>
